import type {
    CrmCustomFieldsConfig,
    CrmDisplayConfigField,
    CrmEntityType,
} from '@plato/data/model/crm-config';
import { lookupCrmFieldDisplay, lookupCrmFieldSchema } from '@plato/data/model/crm-config';
import type { CrmCustomerContactUpdate } from '@plato/data/queries/crm-customer-contact-update';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { get, set } from 'immutable-json-pointer';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerAddressGetDetailOutput } from '../../../server/api/routers/plato-user/query/customer-address.ts';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import { tryParseNumberStrict, tryParseURL } from '../../../utils/string-utils.ts';
import { getCrmFieldLabel, getCrmFieldPlaceholder } from '../utils/crm-field-labels.ts';

type ContextDataTypes = {
    address: CustomerAddressGetDetailOutput;
    customer: CustomerDTO;
    contact: CrmCustomerContactUpdate;
};

export type CrmContext<T extends CrmEntityType = CrmEntityType> = {
    customFieldConfig?: CrmCustomFieldsConfig;
    data?: ContextDataTypes[T] | undefined;
    entityType: T;
    setData: Dispatch<SetStateAction<ContextDataTypes[T]>>;
};

const CrmContext = createContext<CrmContext | undefined>(undefined);

export type CrmContextProviderProps<T extends CrmEntityType> = {
    children?: ReactNode;
    value: CrmContext<T>;
};

export function CrmContextProvider<T extends CrmEntityType>(props: CrmContextProviderProps<T>) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-type-assertion
    return <CrmContext.Provider value={props.value as any}>{props.children}</CrmContext.Provider>;
}

export function useCrmContext<T extends CrmEntityType>(entityType?: T): CrmContext<T> {
    const context = useContext(CrmContext) as CrmContext<T> | undefined;
    if (!context) {
        throw new Error('no CrmContext found, did you forget to wrap in CrmContextProvider?');
    }
    if (entityType && context.entityType !== entityType) {
        throw new Error(`expected entityType ${entityType}, got ${context.entityType}`);
    }
    return context;
}

export function useCrmFieldConfig(config: CrmDisplayConfigField) {
    const { t } = useTranslation('customer-detail');
    const { customFieldConfig, entityType } = useCrmContext();

    const fieldConfig = lookupCrmFieldDisplay(entityType, config, customFieldConfig);

    const normalized: CrmDisplayConfigField = {
        ...fieldConfig,
    };

    if (!fieldConfig.label) {
        normalized.label = getCrmFieldLabel(t, entityType, config.path);
    }
    if (!fieldConfig.placeholder) {
        normalized.placeholder = getCrmFieldPlaceholder(t, entityType, config.path);
    }
    return normalized;
}

export function useCrmFieldRawValue<T>(path: string): [T | undefined, Dispatch<T | undefined>] {
    const { data, setData } = useCrmContext();

    const value = useMemo(() => data && safeGetFieldByPath(data, path), [data, path]) as T;

    const setValue = useCallback(
        (value: unknown) => {
            setData((current) => {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- very hard to track here what is actual type
                if (!current) {
                    return current;
                }
                // only mutate the parent value if the path value has changed
                if (safeGetFieldByPath(current, path) === value) {
                    return current;
                }
                return set(current, path, value);
            });
        },
        [path, setData],
    );

    return [value, setValue];
}

export function useCrmFieldStringValue(path: string): [string, Dispatch<string>] {
    const locale = useResolvedLocale();
    const [value, setValue] = useCrmFieldRawValue(path);
    const { entityType, customFieldConfig } = useCrmContext();

    const validation = useMemo(
        () => lookupCrmFieldSchema(entityType, path, customFieldConfig),
        [customFieldConfig, entityType, path],
    );

    const stringValue = useMemo(() => {
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value.toString();
        }
        if (typeof value === 'boolean') {
            return value.toString();
        }
        if (value instanceof Date) {
            return new Intl.DateTimeFormat(locale, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }).format(value);
        }
        return '';
    }, [locale, value]);

    const setStringValue = useCallback(
        (value: string) => {
            if (!validation) {
                return;
            }
            if (validation.type === 'integer' || validation.type === 'number') {
                setValue(tryParseNumberStrict(value));
            } else if (validation.format === 'uri') {
                setValue(tryParseURL(value, 'https://')?.href ?? value.trim());
            } else {
                setValue(value.trim());
            }
        },
        [setValue, validation],
    );

    return [stringValue, setStringValue];
}

export type UseDraftDataOptions<T> = {
    data: T | undefined;
    disableAutoSave?: boolean;
    save: (data: T) => void;
};

export type UseDraftDataHook<T> = {
    reset: () => void;
    data: T | undefined;
    setData: Dispatch<SetStateAction<T>>;
    save: () => void;
};

export function useDraftData<T>(options: UseDraftDataOptions<T>): UseDraftDataHook<T> {
    const { data: externalData, disableAutoSave, save: externalSave } = options;
    const dataRef = useRef(externalData);
    const staleDataRef = useRef(externalData);
    const autoSaveRef = useRef(!disableAutoSave);
    const [internalData, setInternalData] = useState(externalData);

    useEffect(() => {
        autoSaveRef.current = !disableAutoSave;
    }, [disableAutoSave]);

    useEffect(() => {
        if (externalData !== dataRef.current) {
            dataRef.current = externalData;
            // eslint-disable-next-line react-hooks/set-state-in-effect -- legacy code
            setInternalData(externalData);
        }
        // stale data is a cache for the original unedited data, so that when
        // reset is called, the data is reverted immediately for better UX
        staleDataRef.current = externalData;
    }, [externalData]);

    const save = useCallback(() => {
        if (dataRef.current === undefined) {
            return;
        }
        externalSave(dataRef.current);
    }, [externalSave]);

    const setData = useCallback(
        (value: SetStateAction<T>) => {
            if (!dataRef.current) {
                return;
            }
            let next: T;
            if (typeof value === 'function') {
                next = (value as (prevState: T) => T)(dataRef.current);
            } else {
                next = value;
            }

            dataRef.current = next;
            setInternalData(next);

            if (autoSaveRef.current) {
                save();
            }
        },
        [save],
    );

    const reset = useCallback(() => {
        dataRef.current = staleDataRef.current;
        setInternalData(staleDataRef.current);
    }, []);

    return {
        reset,
        save,
        data: internalData,
        setData,
    };
}

export function safeGetFieldByPath(obj: object, pointer: string): unknown {
    try {
        return get(obj, pointer);
    } catch {
        return undefined;
    }
}
